import React, { useState, useCallback } from "react";

/* --------------------------------------------------------------------
   Notification component – overlay toasts that slide in / out
   Matches brand palette defined in Global.css (uses inline colours).
   ------------------------------------------------------------------ */

// ---------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------

type NotificationType = "success" | "error" | "warning" | "info";

interface NotificationItem {
  id: number;
  type: NotificationType;
  title: string | React.ReactNode;
  message?: string | React.ReactNode;
  duration: number; // ms, 0 means permanent
  isVisible: boolean;
}

interface IconProps {
  size?: number;
  style?: React.CSSProperties;
  className?: string;
}

// ---------------------------------------------------------------------
// Icon SVG components (minimal subset of Heroicons)
// ---------------------------------------------------------------------

const CheckCircleIcon: React.FC<IconProps> = ({
  size = 20,
  style,
  className,
}) => (
  <svg
    width={size}
    height={size}
    fill="currentColor"
    style={style}
    className={className}
    viewBox="0 0 24 24"
  >
    <path
      fillRule="evenodd"
      d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
      clipRule="evenodd"
    />
  </svg>
);

const XCircleIcon: React.FC<IconProps> = ({ size = 20, style, className }) => (
  <svg
    width={size}
    height={size}
    fill="currentColor"
    style={style}
    className={className}
    viewBox="0 0 24 24"
  >
    <path
      fillRule="evenodd"
      d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm-1.72 6.97a.75.75 0 10-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 101.06 1.06L12 13.06l1.72 1.72a.75.75 0 101.06-1.06L13.06 12l1.72-1.72a.75.75 0 10-1.06-1.06L12 10.94l-1.72-1.72z"
      clipRule="evenodd"
    />
  </svg>
);

const ExclamationTriangleIcon: React.FC<IconProps> = ({
  size = 20,
  style,
  className,
}) => (
  <svg
    width={size}
    height={size}
    fill="currentColor"
    style={style}
    className={className}
    viewBox="0 0 24 24"
  >
    <path
      fillRule="evenodd"
      d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z"
      clipRule="evenodd"
    />
  </svg>
);

const InformationCircleIcon: React.FC<IconProps> = ({
  size = 20,
  style,
  className,
}) => (
  <svg
    width={size}
    height={size}
    fill="currentColor"
    style={style}
    className={className}
    viewBox="0 0 24 24"
  >
    <path
      fillRule="evenodd"
      d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm8.706-1.442c1.146-.573 2.437.463 2.126 1.706l-.709 2.836.042-.02a.75.75 0 01.67 1.34l-.04.022c-1.147.573-2.438-.463-2.127-1.706l.71-2.836-.042.02a.75.75 0 11-.671-1.34l.041-.022zM12 9a.75.75 0 100-1.5.75.75 0 000 1.5z"
      clipRule="evenodd"
    />
  </svg>
);

const XMarkIcon: React.FC<IconProps> = ({ size = 16, style, className }) => (
  <svg
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    style={style}
    className={className}
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

// ---------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------

const NotificationComponent: React.FC = () => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);

  // Mapping type → style config (moodboard-inspired premium palette)
  const notificationStyles: Record<
    NotificationType,
    { icon: React.FC<IconProps>; bg: string; txt: string; accent: string; border: string; iconBg: string }
  > = {
    success: {
      icon: CheckCircleIcon,
      bg: "var(--theme-success-bg)",
      txt: "var(--theme-text)",
      accent: "var(--theme-success-text)",
      border: "var(--theme-border)",
      iconBg: "var(--theme-primary-tint)",
    },
    error: {
      icon: XCircleIcon,
      bg: "var(--theme-danger-bg)",
      txt: "var(--theme-text)",
      accent: "var(--theme-danger-text)",
      border: "var(--theme-border)",
      iconBg: "var(--theme-surface-soft)",
    },
    warning: {
      icon: ExclamationTriangleIcon,
      bg: "var(--theme-warning-bg)",
      txt: "var(--theme-text)",
      accent: "var(--theme-warning-text)",
      border: "var(--theme-border)",
      iconBg: "var(--theme-surface-soft)",
    },
    info: {
      icon: InformationCircleIcon,
      bg: "var(--theme-surface-strong)",
      txt: "var(--theme-text)",
      accent: "var(--theme-info-text)",
      border: "var(--theme-border)",
      iconBg: "var(--theme-info-bg)",
    },
  };

  // -------------------------------------------------------------------
  // Public method – could be lifted to context later
  // -------------------------------------------------------------------

  const removeNotification = useCallback((id: number) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, isVisible: false } : n))
    );
    setTimeout(
      () => setNotifications((prev) => prev.filter((n) => n.id !== id)),
      300
    );
  }, []);

  const addNotification = useCallback(
    (
      type: NotificationType,
      title: string | React.ReactNode,
      message?: string | React.ReactNode,
      duration: number = 5000
    ) => {
      const id = Date.now() + Math.random();
      const notif: NotificationItem = {
        id,
        type,
        title,
        message,
        duration,
        isVisible: false,
      };

      setNotifications((prev) => [...prev, notif]);

      // Entrance animation (visibility flag)
      requestAnimationFrame(() =>
        setNotifications((prev) =>
          prev.map((n) => (n.id === id ? { ...n, isVisible: true } : n))
        )
      );

      if (duration > 0) {
        setTimeout(() => removeNotification(id), duration);
      }
    },
    [removeNotification]
  );

  // TEMP: expose to window for quick testing
  (window as any).notify = addNotification;
  // Expose showNotification to window for auth components
  (window as any).showNotification = (
    message: string | React.ReactNode,
    type: NotificationType = "info"
  ) => {
    addNotification(type, message);
  };

  // -------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------

  return (
    <>
      {/* Notification Container - Fixed overlay */}
      <div
        style={{
          position: "fixed",
          top: "24px",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 2000,
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          maxWidth: "420px",
          width: "calc(100% - 32px)",
          pointerEvents: "none",
        }}
      >
        {notifications.map((notification) => {
          const config = notificationStyles[notification.type];
          const IconComponent = config.icon;

          return (
            <div
              key={notification.id}
              style={{
                borderRadius: "16px",
                padding: "16px 20px",
                boxShadow: `
                  0 4px 24px var(--theme-shadow),
                  0 1px 3px var(--theme-shadow)
                `,
                border: `1px solid ${config.border}`,
                transform: notification.isVisible
                  ? "translateY(0)"
                  : "translateY(-20px)",
                opacity: notification.isVisible ? 1 : 0,
                transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
                backgroundColor: config.bg,
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                pointerEvents: "auto",
                fontFamily: "'Raleway', sans-serif",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "14px",
                }}
              >
                {/* Icon with background circle */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 32,
                    height: 32,
                    borderRadius: "10px",
                    backgroundColor: config.iconBg,
                    flexShrink: 0,
                  }}
                >
                  <IconComponent
                    size={18}
                    style={{
                      color: config.accent,
                    }}
                  />
                </div>

                <div style={{ flex: 1, minWidth: 0, paddingTop: 4 }}>
                  <h4
                    style={{
                      margin: 0,
                      fontWeight: 600,
                      fontSize: "14px",
                      lineHeight: "1.4",
                      color: config.txt,
                      letterSpacing: "-0.01em",
                    }}
                  >
                    {notification.title}
                  </h4>

                  {notification.message && (
                    <p
                      style={{
                        margin: "6px 0 0 0",
                        fontSize: "13px",
                        lineHeight: "1.5",
                        color: config.txt,
                        opacity: 0.75,
                      }}
                    >
                      {notification.message}
                    </p>
                  )}
                </div>

                {/* Close button - pill style */}
                <button
                  onClick={() => removeNotification(notification.id)}
                  style={{
                    background: "var(--theme-primary-tint)",
                    border: "none",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 6,
                    width: 28,
                    height: 28,
                    borderRadius: "8px",
                    color: config.txt,
                    opacity: 0.6,
                    transition: "all 0.2s ease",
                    flexShrink: 0,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = "1";
                    e.currentTarget.style.background = "var(--theme-primary-tint-strong)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = "0.6";
                    e.currentTarget.style.background = "var(--theme-primary-tint)";
                  }}
                >
                  <XMarkIcon size={14} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* CSS Animations for mobile responsiveness */}
      <style>{`
        @media (max-width: 768px) {
          div[style*="position: fixed"][style*="top: 24px"] {
            left: 16px !important;
            right: 16px !important;
            transform: none !important;
            max-width: none !important;
          }
        }
      `}</style>
    </>
  );
};

export default NotificationComponent;
